import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
// CheckCircleOutlineIcon removed — using RocketLaunchIcon for approve & go live
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import {
  approveBudgetCenter,
  fetchBudgetCenter,
  fetchBudgetSubmissions,
  markBudgetCenterLive,
} from '@src/api/actions/dashboard/clientBudgets';
import defaultClient from '@src/api/clients/default';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';
import paths from '@src/paths';

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatCurrency(value) {
  const num = Number(value || 0);
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDateTime(dateString) {
  if (!dateString) return '';
  const d = new Date(dateString);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function getStatusColor(status) {
  switch (status) {
    case 'pending_approval': return 'warning';
    case 'turn_off_requested': return 'error';
    case 'approved': return 'info';
    case 'active': return 'success';
    case 'draft': return 'default';
    case 'completed': return 'success';
    case 'archived': return 'success';
    default: return 'default';
  }
}

function getStatusLabel(status) {
  switch (status) {
    case 'pending_approval': return 'Pending Approval';
    case 'turn_off_requested': return 'Turn Off Request';
    case 'approved': return 'Approved';
    case 'active': return 'Live';
    case 'draft': return 'Draft';
    case 'completed': return 'Completed';
    case 'archived': return 'Completed';
    default: return status;
  }
}

function StatChip({ label, value, color = 'default' }) {
  return (
    <Chip
      label={`${label}: ${value}`}
      color={color}
      size="small"
      variant={color === 'default' ? 'outlined' : 'filled'}
    />
  );
}

function rowKey(row) {
  return `${row.client_id}-${row.period}`;
}

function formatCurrencyShort(value) {
  const num = Number(value || 0);
  if (Math.abs(num) >= 1000) {
    return `$${(num / 1000).toFixed(num % 1000 === 0 ? 0 : 1)}k`;
  }
  return `$${num.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function changeTypeChip(change, idx) {
  const segLabel = change.label || '';
  if (change.type === 'new') {
    return (
      <Tooltip key={idx} title={`New: ${segLabel} — ${formatCurrency(change.amount)}`}>
        <Chip
          label={`New ${formatCurrencyShort(change.amount)}`}
          size="small"
          color="info"
          variant="filled"
        />
      </Tooltip>
    );
  }
  if (change.type === 'increase') {
    return (
      <Tooltip key={idx} title={`${segLabel}: ${formatCurrency(change.from)} → ${formatCurrency(change.to)}`}>
        <Chip
          label={`↑ ${formatCurrencyShort(change.delta)}`}
          size="small"
          color="success"
          variant="filled"
        />
      </Tooltip>
    );
  }
  if (change.type === 'decrease') {
    const absDelta = String(Number(change.delta || 0) * -1);
    return (
      <Tooltip key={idx} title={`${segLabel}: ${formatCurrency(change.from)} → ${formatCurrency(change.to)}`}>
        <Chip
          label={`↓ ${formatCurrencyShort(absDelta)}`}
          size="small"
          color="warning"
          variant="filled"
        />
      </Tooltip>
    );
  }
  if (change.type === 'turn_off') {
    return (
      <Tooltip key={idx} title={`Turn off: ${segLabel} (${formatCurrency(String(Number(change.delta || 0) * -1))})`}>
        <Chip
          label="Turn off"
          size="small"
          color="error"
          variant="filled"
        />
      </Tooltip>
    );
  }
  return null;
}

function ChangeTypesCell({ changes }) {
  if (!changes || changes.length === 0) {
    return <Typography variant="caption" color="text.secondary">No change</Typography>;
  }
  const MAX_VISIBLE = 3;
  const visible = changes.slice(0, MAX_VISIBLE);
  const overflow = changes.length - visible.length;
  const overflowTooltip = changes
    .slice(MAX_VISIBLE)
    .map((c) => {
      if (c.type === 'increase') return `↑ ${c.label}: ${formatCurrency(c.from)} → ${formatCurrency(c.to)}`;
      if (c.type === 'decrease') return `↓ ${c.label}: ${formatCurrency(c.from)} → ${formatCurrency(c.to)}`;
      if (c.type === 'turn_off') return `Turn off ${c.label}`;
      if (c.type === 'new') return `New ${c.label}: ${formatCurrency(c.amount)}`;
      return c.label;
    })
    .join('\n');
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ maxWidth: 220 }}>
      {visible.map((c, i) => changeTypeChip(c, i))}
      {overflow > 0 && (
        <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{overflowTooltip}</span>}>
          <Chip label={`+${overflow}`} size="small" variant="outlined" />
        </Tooltip>
      )}
    </Stack>
  );
}

function uniqueChannelNames(segments) {
  const names = (segments || [])
    .map((s) => s.channel_name)
    .filter(Boolean);
  return Array.from(new Set(names));
}

// ─── Page ───────────────────────────────────────────────────────────────────

export default function BudgetSubmissionsPage() {
  const {
    options: clientOptions,
    handleInputChange: handleClientSearch,
    findOptionByValue,
  } = useClientAutocomplete();

  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Filters
  const [filterClientId, setFilterClientId] = useState('');
  const [filterStatus, setFilterStatus] = useState('needs_review');
  const [filterPeriod, setFilterPeriod] = useState('');

  // Expandable rows
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [manualCollapsed, setManualCollapsed] = useState(new Set());

  // Action loading
  const [actionLoading, setActionLoading] = useState(null);

  // Approve & Go Live dialog
  const [approveDialog, setApproveDialog] = useState(null);
  const [approving, setApproving] = useState(false);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkApproving, setBulkApproving] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const showSnack = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  // ─── Expand / Collapse ────────────────────────────────────────────

  const toggleExpand = useCallback((id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        setManualCollapsed((mc) => new Set([...mc, id]));
      } else {
        next.add(id);
        setManualCollapsed((mc) => {
          const nm = new Set(mc);
          nm.delete(id);
          return nm;
        });
      }
      return next;
    });
  }, []);

  const isExpanded = useCallback(
    (id) => expandedIds.has(id) && !manualCollapsed.has(id),
    [expandedIds, manualCollapsed]
  );

  // ─── Data Loading ─────────────────────────────────────────────────

  const loadSubmissions = useCallback(async (pageNum = page) => {
    setLoading(true);
    try {
      const params = { page: pageNum };
      if (filterClientId) params.client_id = filterClientId;
      if (filterStatus) params.status = filterStatus;
      if (filterPeriod) params.period_filter = filterPeriod;
      const res = await fetchBudgetSubmissions(params);
      const results = res.data?.results || [];
      setSubmissions(results);
      setTotalCount(res.data?.count || 0);
      setTotalPages(Math.ceil((res.data?.count || 0) / 50));

      // Auto-expand anything that needs review
      const pendingIds = new Set(
        results
          .filter((s) => (
            s.status === 'pending_approval'
            || s.status === 'turn_off_requested'
            || (s.pending_turn_offs && s.pending_turn_offs.length > 0)
          ))
          .map((s) => rowKey(s))
      );
      setExpandedIds(pendingIds);
      setManualCollapsed(new Set());
      setSelectedIds(new Set());
    } catch {
      setSubmissions([]);
    } finally {
      setLoading(false);
    }
  }, [filterClientId, filterPeriod, filterStatus, page]);

  useEffect(() => {
    setPage(1);
    loadSubmissions(1);
  }, [filterClientId, filterPeriod, filterStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadSubmissions(page);
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  // Drop acted-on rows from the list in place (no refetch) so the table
  // updates instantly. The row no longer matches the active filter once
  // its status changes, so removing it mirrors what a reload would show.
  const removeRowsLocally = useCallback((keys) => {
    const keySet = keys instanceof Set ? keys : new Set(keys);
    if (keySet.size === 0) return;
    setSubmissions((prev) => prev.filter((s) => !keySet.has(rowKey(s))));
    setTotalCount((prev) => Math.max(0, prev - keySet.size));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      keySet.forEach((k) => next.delete(k));
      return next;
    });
    setExpandedIds((prev) => {
      const next = new Set(prev);
      keySet.forEach((k) => next.delete(k));
      return next;
    });
    setManualCollapsed((prev) => {
      const next = new Set(prev);
      keySet.forEach((k) => next.delete(k));
      return next;
    });
  }, []);

  // ─── Stats ────────────────────────────────────────────────────────

  const stats = useMemo(() => {
    const pending = submissions.filter((s) => s.status === 'pending_approval').length;
    const approved = submissions.filter((s) => s.status === 'approved').length;
    const active = submissions.filter((s) => s.status === 'active').length;
    const draft = submissions.filter((s) => s.status === 'draft').length;
    return { pending, approved, active, draft, total: totalCount };
  }, [submissions, totalCount]);

  // ─── Actions ──────────────────────────────────────────────────────

  const approveRow = useCallback(async (row) => {
    // Step 1: Approve
    const centerRes = await fetchBudgetCenter({
      client_id: row.client_id,
      period: row.period,
    });
    const pageData = centerRes.data;
    const draftBudgetIds = new Set(pageData?.draft_budget_ids || []);
    // Only send draft/pending segments — live segments don't need re-approval
    const draftSegments = (pageData?.segments || [])
      .filter((seg) => {
        const wf = String(seg.workflow_status || '').toLowerCase();
        return wf === 'draft' || wf === 'pending_approval' || draftBudgetIds.has(seg.budget_id);
      });

    if (draftSegments.length) {
      const segments = draftSegments.map((seg) => ({
        budget_id: seg.budget_id || null,
        source_budget_id: seg.source_budget_id || null,
        vertical: seg.vertical || seg.vertical_data?.id || '',
        vertical_ids: seg.vertical ? [Number(seg.vertical)] : (seg.vertical_data?.id ? [seg.vertical_data.id] : []),
        location: seg.location || seg.location_data?.id || '',
        location_ids: seg.location ? [Number(seg.location)] : (seg.location_data?.id ? [seg.location_data.id] : []),
        channel: seg.channel || seg.channel_data?.id || '',
        amount: seg.amount || '0',
        notes: seg.notes || '',
      }));

      await approveBudgetCenter({
        client_id: row.client_id,
        period: row.period,
        segments,
      });
    }

    // Step 2: Only mark live for current month or already-approved budgets
    const shouldGoLive = row.is_current_month || row.status === 'approved';
    if (shouldGoLive) {
      await markBudgetCenterLive({
        client_id: row.client_id,
        period: row.period,
      });
    }
    return { wentLive: shouldGoLive };
  }, []);

  const handleApproveAndLive = async () => {
    if (!approveDialog) return;
    setApproving(true);
    try {
      const { wentLive } = await approveRow(approveDialog);
      if (wentLive) {
        showSnack(`${approveDialog.client_name} ${approveDialog.period_label} budget approved and live.`);
      } else {
        showSnack(`${approveDialog.client_name} ${approveDialog.period_label} budget approved. It will go live when the month starts.`);
      }
      removeRowsLocally([rowKey(approveDialog)]);
      setApproveDialog(null);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.response?.data?.segments || 'Failed to approve.';
      showSnack(typeof detail === 'string' ? detail : JSON.stringify(detail), 'error');
    } finally {
      setApproving(false);
    }
  };

  // ─── Bulk Selection ───────────────────────────────────────────────

  const approvableSubmissions = useMemo(
    () => submissions.filter((s) => s.status === 'pending_approval' || s.status === 'approved'),
    [submissions]
  );

  const isRowApprovable = (row) =>
    row.status === 'pending_approval' || row.status === 'approved';

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (approvableSubmissions.length === 0) return;
    if (selectedIds.size === approvableSubmissions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(approvableSubmissions.map((s) => rowKey(s))));
    }
  };

  const handleBulkApprove = async () => {
    const selectedRows = submissions.filter((s) => selectedIds.has(rowKey(s)));
    if (selectedRows.length === 0) return;
    const total = selectedRows.reduce((sum, r) => sum + Number(r.total_amount || 0), 0);
    const liveCount = selectedRows.filter((r) => r.is_current_month || r.status === 'approved').length;
    const parts = [
      `Approve ${selectedRows.length} budget${selectedRows.length > 1 ? 's' : ''} totaling ${formatCurrency(total)}?`,
    ];
    if (liveCount > 0) {
      parts.push(`${liveCount} will go live immediately.`);
    }
    if (!window.confirm(parts.join('\n\n'))) {
      return;
    }
    setBulkApproving(true);
    const succeededKeys = [];
    let failCount = 0;
    for (const row of selectedRows) {
      try {
        await approveRow(row);
        succeededKeys.push(rowKey(row));
      } catch {
        failCount += 1;
      }
    }
    setBulkApproving(false);
    const successCount = succeededKeys.length;
    if (failCount > 0) {
      showSnack(`${successCount} approved, ${failCount} failed.`, 'warning');
    } else {
      showSnack(`${successCount} budget${successCount > 1 ? 's' : ''} approved.`);
    }
    removeRowsLocally(succeededKeys);
  };

  const handleApproveTurnOff = async (turnOff) => {
    setActionLoading(`turnoff-${turnOff.adjustment_id}`);
    try {
      await defaultClient.request({
        url: `dashboard/client-budgets/${turnOff.budget_id}/adjustments/${turnOff.adjustment_id}/approve/`,
        method: 'POST',
      });
      showSnack('Turn-off approved.');
      return true;
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to approve turn-off.';
      showSnack(detail, 'error');
      return false;
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectTurnOff = async (turnOff) => {
    setActionLoading(`reject-turnoff-${turnOff.adjustment_id}`);
    try {
      await defaultClient.request({
        url: `dashboard/client-budgets/${turnOff.budget_id}/adjustments/${turnOff.adjustment_id}/`,
        method: 'DELETE',
      });
      showSnack('Turn-off request rejected.');
      return true;
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.response?.data?.adjustment_id || 'Failed to reject turn-off.';
      showSnack(detail, 'error');
      return false;
    } finally {
      setActionLoading(null);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────

  const COL_COUNT = 12; // checkbox + expand + 10 data columns

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="h4">Budget Submissions</Typography>
          {stats.pending > 0 && (
            <Chip label={`${stats.pending} pending`} color="warning" size="small" />
          )}
        </Stack>
      </Box>

      {/* Quick Stats */}
      <Stack direction="row" spacing={2} mb={2} flexWrap="wrap">
        <StatChip label="Total" value={stats.total} />
        <StatChip label="Pending Approval" value={stats.pending} color="warning" />
        <StatChip label="Approved" value={stats.approved} color="info" />
        <StatChip label="Live" value={stats.active} color="success" />
        <StatChip label="Draft" value={stats.draft} />
      </Stack>

      <Card>
        <CardContent>
          {/* Filters */}
          <Stack direction="row" spacing={2} mb={2} alignItems="center" flexWrap="wrap">
            <Autocomplete
              options={clientOptions}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(o, v) => o.value === v.value}
              value={findOptionByValue(filterClientId)}
              onChange={(_, val) => setFilterClientId(val ? String(val.value) : '')}
              onInputChange={handleClientSearch}
              renderInput={(params) => (
                <TextField {...params} label="Client" size="small" sx={{ minWidth: 220 }} />
              )}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="needs_review">Needs Review</MenuItem>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="pending_approval">Pending Approval</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="active">Live</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="turn_off_requested">Turn Off Requests</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Period</InputLabel>
              <Select
                label="Period"
                value={filterPeriod}
                onChange={(e) => setFilterPeriod(e.target.value)}
              >
                <MenuItem value="">All Months</MenuItem>
                <MenuItem value="current_month">This Month</MenuItem>
                <MenuItem value="future">Future</MenuItem>
                <MenuItem value="past">Past</MenuItem>
              </Select>
            </FormControl>
            <Button variant="outlined" onClick={() => loadSubmissions(1)} disabled={loading}>
              {loading ? <CircularProgress size={18} /> : 'Refresh'}
            </Button>

            {/* Bulk approve */}
            {selectedIds.size > 0 && (
              <Button
                variant="contained"
                color="success"
                startIcon={bulkApproving ? <CircularProgress size={16} color="inherit" /> : <RocketLaunchIcon />}
                onClick={handleBulkApprove}
                disabled={bulkApproving}
              >
                Approve {selectedIds.size} Selected
              </Button>
            )}
          </Stack>

          {loading ? (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress />
            </Box>
          ) : submissions.length === 0 ? (
            <Alert severity="info">No budget submissions found matching your filters.</Alert>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 600 } }}>
                      <TableCell padding="checkbox">
                        {approvableSubmissions.length > 0 && (
                          <Checkbox
                            size="small"
                            checked={selectedIds.size === approvableSubmissions.length && approvableSubmissions.length > 0}
                            indeterminate={selectedIds.size > 0 && selectedIds.size < approvableSubmissions.length}
                            onChange={toggleSelectAll}
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}
                      </TableCell>
                      <TableCell width={40} />
                      <TableCell>Client</TableCell>
                      <TableCell>Period</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Change</TableCell>
                      <TableCell>Segments</TableCell>
                      <TableCell align="right">Total Budget</TableCell>
                      <TableCell>Comments</TableCell>
                      <TableCell>Last Activity</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {submissions.map((row) => {
                      const id = rowKey(row);
                      const expanded = isExpanded(id);
                      const approvable = isRowApprovable(row);
                      const isSelected = selectedIds.has(id);
                      return (
                        <React.Fragment key={id}>
                          {/* ── Main row ── */}
                          <TableRow
                            hover
                            sx={{
                              cursor: 'pointer',
                              '& > td': { borderBottom: expanded ? 'none' : undefined },
                            }}
                            onClick={() => toggleExpand(id)}
                          >
                            <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                              {approvable && (
                                <Checkbox
                                  size="small"
                                  checked={isSelected}
                                  onChange={() => toggleSelect(id)}
                                />
                              )}
                            </TableCell>
                            <TableCell sx={{ pr: 0 }}>
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleExpand(id); }}>
                                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              </IconButton>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {row.client_name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Stack spacing={0.25}>
                                <Typography variant="body2">{row.period_label}</Typography>
                                {row.is_past_due && row.status !== 'active' ? (
                                  <Chip label="Past Due" color="error" size="small" variant="outlined" sx={{ width: 'fit-content' }} />
                                ) : row.is_current_month ? (
                                  <Chip label="This Month" color="warning" size="small" variant="outlined" sx={{ width: 'fit-content' }} />
                                ) : (
                                  <Typography variant="caption" color="text.secondary">Upcoming</Typography>
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={getStatusLabel(row.status)}
                                color={getStatusColor(row.status)}
                                size="small"
                                variant="filled"
                              />
                            </TableCell>
                            <TableCell>
                              <ChangeTypesCell changes={row.change_types} />
                            </TableCell>
                            <TableCell>
                              <Stack spacing={0.25}>
                                <Typography variant="body2">{row.segment_count}</Typography>
                                {(() => {
                                  const channels = uniqueChannelNames(row.segments);
                                  if (channels.length === 0) return null;
                                  return (
                                    <Typography variant="caption" color="text.secondary" noWrap>
                                      {channels.join(' · ')}
                                    </Typography>
                                  );
                                })()}
                              </Stack>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight={600}>
                                {formatCurrency(row.total_amount)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {row.comment_count > 0 ? (
                                <Chip
                                  icon={<ChatBubbleOutlineIcon sx={{ fontSize: 14 }} />}
                                  label={row.comment_count}
                                  size="small"
                                  variant="outlined"
                                  color="secondary"
                                />
                              ) : (
                                <Typography color="text.secondary" variant="caption">&mdash;</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {row.last_activity ? (
                                <Tooltip title={row.last_activity.summary || ''}>
                                  <Stack spacing={0.25}>
                                    <Typography variant="caption" noWrap sx={{ maxWidth: 200 }}>
                                      {row.last_activity.summary}
                                    </Typography>
                                    <Typography color="text.secondary" variant="caption">
                                      {row.last_activity.actor?.name || row.last_activity.actor?.email || 'System'} &middot; {formatDateTime(row.last_activity.created_at)}
                                    </Typography>
                                  </Stack>
                                </Tooltip>
                              ) : (
                                <Typography color="text.secondary" variant="caption">&mdash;</Typography>
                              )}
                            </TableCell>
                            <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                              <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                {row.status === 'pending_approval' || row.status === 'approved' ? (
                                  <Button
                                    size="small"
                                    color="success"
                                    variant="contained"
                                    startIcon={<RocketLaunchIcon />}
                                    disabled={actionLoading !== null}
                                    onClick={() => setApproveDialog({
                                      client_id: row.client_id,
                                      period: row.period,
                                      client_name: row.client_name,
                                      period_label: row.period_label,
                                      total_amount: row.total_amount,
                                      segment_count: row.segment_count,
                                      status: row.status,
                                      is_current_month: row.is_current_month,
                                    })}
                                  >
                                    {row.status === 'approved'
                                      ? 'Go Live'
                                      : row.is_current_month ? 'Approve & Go Live' : 'Approve'}
                                  </Button>
                                ) : null}
                                {(row.pending_turn_offs || []).length > 0 ? (
                                  <>
                                    <Tooltip title={(row.pending_turn_offs || []).map((t) => `${t.segment_label}: ${formatCurrency(t.amount_delta)}`).join(', ')}>
                                      <Button
                                        size="small"
                                        color="error"
                                        variant="contained"
                                        disabled={actionLoading !== null}
                                        onClick={async () => {
                                          let ok = true;
                                          for (const turnOff of row.pending_turn_offs) {
                                            if (!(await handleApproveTurnOff(turnOff))) ok = false;
                                          }
                                          if (ok) removeRowsLocally([rowKey(row)]);
                                          else loadSubmissions(page);
                                        }}
                                      >
                                        {actionLoading?.startsWith('turnoff-')
                                          ? <CircularProgress size={14} color="inherit" />
                                          : `Approve Turn Off (${row.pending_turn_offs.length})`}
                                      </Button>
                                    </Tooltip>
                                    <Button
                                      size="small"
                                      color="warning"
                                      variant="outlined"
                                      disabled={actionLoading !== null}
                                      onClick={async () => {
                                        let ok = true;
                                        for (const turnOff of row.pending_turn_offs) {
                                          if (!(await handleRejectTurnOff(turnOff))) ok = false;
                                        }
                                        if (ok) removeRowsLocally([rowKey(row)]);
                                        else loadSubmissions(page);
                                      }}
                                    >
                                      {actionLoading?.startsWith('reject-turnoff-')
                                        ? <CircularProgress size={14} color="inherit" />
                                        : `Reject Turn Off`}
                                    </Button>
                                  </>
                                ) : null}
                                <Tooltip title="Open in Budget Center">
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    href={`${paths.budgetCenter}?client_id=${row.client_id}&period=${row.period}`}
                                    startIcon={<OpenInNewIcon />}
                                  >
                                    View
                                  </Button>
                                </Tooltip>
                              </Stack>
                            </TableCell>
                          </TableRow>

                          {/* ── Expandable detail row ── */}
                          <TableRow>
                            <TableCell colSpan={COL_COUNT} sx={{ p: 0, border: expanded ? undefined : 'none' }}>
                              <Collapse in={expanded} timeout="auto" unmountOnExit>
                                <Box sx={{ px: 3, py: 2, bgcolor: 'action.hover' }}>
                                  <Stack spacing={2}>
                                    {/* Segment detail table */}
                                    <Box>
                                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        Budget Segments
                                      </Typography>
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow sx={{ '& th': { fontWeight: 600, py: 0.75 } }}>
                                            <TableCell>Vertical</TableCell>
                                            <TableCell>Location</TableCell>
                                            <TableCell>Channel</TableCell>
                                            <TableCell align="right">Starting</TableCell>
                                            <TableCell align="right">Adjustments</TableCell>
                                            <TableCell align="right">Effective</TableCell>
                                            <TableCell>Status</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {(row.segments || []).map((seg, i) => (
                                            <TableRow key={i} sx={{ '& td': { py: 0.75 } }}>
                                              <TableCell>{seg.vertical_name || 'All'}</TableCell>
                                              <TableCell>{seg.location_name || 'All'}</TableCell>
                                              <TableCell>{seg.channel_name || <Typography variant="caption" color="text.secondary">All</Typography>}</TableCell>
                                              <TableCell align="right">
                                                <Typography variant="body2">
                                                  {formatCurrency(seg.amount)}
                                                </Typography>
                                              </TableCell>
                                              <TableCell align="right">
                                                <Typography variant="body2" sx={{ color: Number(seg.adjustment_total || 0) < 0 ? 'error.main' : Number(seg.adjustment_total || 0) > 0 ? 'success.main' : 'text.secondary' }}>
                                                  {Number(seg.adjustment_total || 0) !== 0 ? formatCurrency(seg.adjustment_total) : '\u2014'}
                                                </Typography>
                                              </TableCell>
                                              <TableCell align="right">
                                                <Typography variant="body2" fontWeight={600}>
                                                  {formatCurrency(seg.effective_amount || seg.amount)}
                                                </Typography>
                                              </TableCell>
                                              <TableCell>
                                                <Stack direction="row" spacing={0.5}>
                                                  <Chip
                                                    label={getStatusLabel(seg.workflow_status)}
                                                    color={getStatusColor(seg.workflow_status)}
                                                    size="small"
                                                    variant="outlined"
                                                  />
                                                  {seg.has_pending_turn_off ? (
                                                    <Chip
                                                      label="Turn Off Pending"
                                                      color="error"
                                                      size="small"
                                                      variant="outlined"
                                                    />
                                                  ) : null}
                                                </Stack>
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </Box>

                                    {/* Pending turn-offs */}
                                    {(row.pending_turn_offs || []).length > 0 ? (
                                      <Alert severity="error" variant="outlined" sx={{ py: 0.5 }}>
                                        <Typography variant="subtitle2" gutterBottom>
                                          Pending Turn-Off Request{row.pending_turn_offs.length > 1 ? 's' : ''}
                                        </Typography>
                                        {row.pending_turn_offs.map((turnOff) => (
                                          <Typography key={turnOff.adjustment_id} variant="body2">
                                            {turnOff.segment_label}: {formatCurrency(turnOff.amount_delta)} adjustment
                                          </Typography>
                                        ))}
                                      </Alert>
                                    ) : null}

                                    {/* Metadata */}
                                    <Stack direction="row" spacing={3} sx={{ pt: 0.5 }}>
                                      {row.submitted_at ? (
                                        <Typography variant="caption" color="text.secondary">
                                          Submitted {formatDateTime(row.submitted_at)}
                                        </Typography>
                                      ) : null}
                                      {row.last_activity?.actor ? (
                                        <Typography variant="caption" color="text.secondary">
                                          By {row.last_activity.actor.name || row.last_activity.actor.email}
                                        </Typography>
                                      ) : null}
                                      {row.comment_count > 0 ? (
                                        <Typography variant="caption" color="text.secondary">
                                          {row.comment_count} comment{row.comment_count !== 1 ? 's' : ''} in discussion
                                        </Typography>
                                      ) : null}
                                    </Stack>
                                  </Stack>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {totalPages > 1 && (
                <Box display="flex" justifyContent="center" mt={2}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, p) => setPage(p)}
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Approve & Go Live Confirmation Dialog */}
      <Dialog fullWidth maxWidth="sm" open={Boolean(approveDialog)} onClose={() => setApproveDialog(null)}>
        <DialogTitle>
          {approveDialog?.status === 'approved'
            ? 'Go Live'
            : approveDialog?.is_current_month ? 'Approve & Go Live' : 'Approve Budget'}
        </DialogTitle>
        <DialogContent>
          {approveDialog ? (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography variant="body2">
                {approveDialog.status === 'approved'
                  ? <>Make the <strong>{approveDialog.period_label}</strong> budget for <strong>{approveDialog.client_name}</strong> live?</>
                  : approveDialog.is_current_month
                    ? <>Approve and make the <strong>{approveDialog.period_label}</strong> budget for <strong>{approveDialog.client_name}</strong> live?</>
                    : <>Approve the <strong>{approveDialog.period_label}</strong> budget for <strong>{approveDialog.client_name}</strong>?</>
                }
              </Typography>
              <Stack direction="row" spacing={3}>
                <Box>
                  <Typography color="text.secondary" variant="caption">Total Budget</Typography>
                  <Typography variant="h6">{formatCurrency(approveDialog.total_amount)}</Typography>
                </Box>
                <Box>
                  <Typography color="text.secondary" variant="caption">Segments</Typography>
                  <Typography variant="h6">{approveDialog.segment_count}</Typography>
                </Box>
              </Stack>
              <Alert severity={approveDialog.is_current_month || approveDialog.status === 'approved' ? 'warning' : 'info'}>
                {approveDialog.is_current_month || approveDialog.status === 'approved'
                  ? 'The budget will immediately become active and the client will be notified.'
                  : 'The budget will be approved and ready to go live when the month starts.'}
              </Alert>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialog(null)}>Cancel</Button>
          <Button
            color="success"
            variant="contained"
            onClick={handleApproveAndLive}
            disabled={approving}
            startIcon={approving ? <CircularProgress size={16} color="inherit" /> : <RocketLaunchIcon />}
          >
            {approveDialog?.status === 'approved'
              ? 'Confirm — Go Live'
              : approveDialog?.is_current_month ? 'Confirm — Approve & Go Live' : 'Confirm — Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        open={snackbar.open}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
